import { useId } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { formatCents } from '../v2/shared/formatCents';

import { type HeldCharge, WRITE_OFF_REASONS, type WriteOffReason } from './heldCharges';

export interface WriteOffHeldChargeDialogProps {
  open: boolean;
  /** Kept after close so the content holds still through the exit transition. */
  charge: HeldCharge | null;
  /** Subject language, lower case: "patient" or "client". */
  subject: string;
  reason: WriteOffReason | null;
  note: string;
  /** The server's refusal, in its own words. */
  error: string | null;
  submitting: boolean;
  onReasonChange: (reason: WriteOffReason) => void;
  onNoteChange: (note: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const isWriteOffReason = (value: string): value is WriteOffReason =>
  WRITE_OFF_REASONS.some(option => option.value === value);

/**
 * Write off a charge held after a refund (gdn-4242). The amount is what the
 * charge owes — never edited here — and the write-offs route requires a
 * reason, so confirming waits for one.
 *
 * Mount it once and toggle `open`: MUI's transitions need the dialog to
 * stay mounted.
 */
export const WriteOffHeldChargeDialog = ({
  open,
  charge,
  subject,
  reason,
  note,
  error,
  submitting,
  onReasonChange,
  onNoteChange,
  onCancel,
  onConfirm,
}: WriteOffHeldChargeDialogProps) => {
  const reasonLabelId = useId();
  const amount = charge ? formatCents(charge.amountCents) : '';
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Write off held charge</DialogTitle>
      <DialogContent>
        {charge && (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              {charge.label} · {amount}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Writes off {amount}. The {subject} won&apos;t be billed for it.
            </Typography>
            <FormControl required>
              <FormLabel id={reasonLabelId} sx={{ fontSize: '0.875rem' }}>
                Reason
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby={reasonLabelId}
                value={reason ?? ''}
                onChange={event => {
                  if (isWriteOffReason(event.target.value)) onReasonChange(event.target.value);
                }}
              >
                {WRITE_OFF_REASONS.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <TextField
              label="Note (optional)"
              size="small"
              fullWidth
              value={note}
              onChange={event => onNoteChange(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={!reason || submitting}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {submitting ? 'Writing off…' : `Write off ${amount}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
